import { Button, Divider, Modal, Stack, Text, Title } from "@mantine/core";
import {
  IconArrowRight,
  IconMapPinFilled,
  IconWorld,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import jrmLogo from "../../assets/jobsRmine logo.svg";
import { Country } from "../../types";
import {
  CountryPickerPanel,
  PopularCountryGrid,
} from "./country-picker-panel";
import styles from "./country-selector.module.css";

interface CountrySelectionModalProps {
  opened: boolean;
  countries: Country[];
  popularCountries: Country[];
  loading: boolean;
  onSelect: (country: Country) => void;
  onClose: () => void;
}

export function CountrySelectionModal({
  opened,
  countries,
  popularCountries,
  loading,
  onSelect,
  onClose,
}: CountrySelectionModalProps) {
  const [selected, setSelected] = useState<Country | null>(null);

  useEffect(() => {
    if (opened) setSelected(null);
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={900}
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.72, blur: 2 }}
      title={
        <img
          src={jrmLogo}
          className={styles.modalLogo}
          alt="jobsRmine"
        />
      }
      closeButtonProps={{
        "aria-label": "Use United States as default",
        icon: <IconX size={25} stroke={3} />,
        color: "#000",
      }}
      styles={{ body: { padding: "0 28px 28px" } }}
    >
      <div className={styles.modalHeader}>
        <div>
          <Title order={2} className={styles.modalTitle}>
            Where are you looking for jobs?
          </Title>
          <Text c="dimmed" mt={6}>
            We couldn&apos;t automatically detect your location. Select your
            country to see relevant opportunities.
          </Text>
        </div>
        <div className={styles.globeIllustration} aria-hidden="true">
          <IconWorld className={styles.globeIcon} stroke={1.25} />
          <span className={styles.globePin}>
            <IconMapPinFilled />
          </span>
        </div>
      </div>

      <div className={styles.modalBody}>
        <CountryPickerPanel
          countries={countries}
          popularCountries={popularCountries}
          selectedCode={selected?.code}
          onChange={setSelected}
          loading={loading}
          showPopular={false}
        />
        <Stack justify="center" gap="md" className={styles.modalAction}>
          <PopularCountryGrid
            countries={popularCountries}
            selectedCode={selected?.code}
            onChange={setSelected}
          />
          <Divider label="Your choice" labelPosition="center" />
          <Button
            color="dark"
            size="lg"
            radius="md"
            disabled={!selected}
            rightSection={<IconArrowRight size={18} />}
            onClick={() => selected && onSelect(selected)}
          >
            {selected
              ? `Find jobs in ${selected.countryName}`
              : "Choose a country"}
          </Button>
          <Text c="dimmed" size="xs" ta="center">
            You can change this later from the country selector in the header.
          </Text>
        </Stack>
      </div>
    </Modal>
  );
}
